'use client';
import React, { useEffect } from 'react';
import styled from '@benzinga/themetron';
import Marquee from 'react-fast-marquee';
import { Sparkline } from '@benzinga/core-ui';
import { getClassnameByValue } from '@benzinga/frontend-utils';
import { CoinInput, CryptoDotComCoinResponse } from '../entities';
import {
  formatPrice,
  formatPricesToChartPoints,
  getChartColorFromPrices,
  formatPercentage,
  CryptoDotComLink,
  getCoinIcon,
  getCoinsData,
} from '../utils';

export interface CoinMarqueeProps {
  coins: CoinInput[];
}

export const CoinMarquee: React.FC<CoinMarqueeProps> = ({ coins }) => {
  const [data, setData] = React.useState<CryptoDotComCoinResponse[] | CoinInput[]>(coins);

  useEffect(() => {
    getCoinsData(coins).then(data => setData(data));
  }, [coins]);

  if (data?.length === 0) {
    return null;
  }

  return (
    <CoinMarqueeContainer className="coin-marquee-container">
      <div className="coin-marquee-wrapper">
        <Marquee className="marquee" gradient={false} pauseOnHover={true} speed={80}>
          {(data as CryptoDotComCoinResponse[]).map(coin => (
            <div className="marquee-item" key={`coin-marquee-${coin.slug}`}>
              <a
                data-action="Crypto.com Coin Marquee Item Click"
                data-category="Outbound Links"
                href={`https://crypto.com/price/${coin?.slug}?utm_medium=widget&utm_campaign=CoinMarqueeModern&utm_source=www.benzinga.com&utm_id=${coin?.slug}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <div className="hover-overlay">
                  <span className="hover-overlay-text">Track Price Now</span>
                </div>
                <div className="coin-info-left-section">
                  <img alt={coin.name} className="coin-image" src={getCoinIcon(coin?.slug)} />
                  <div className="coin-name-symbol-container">
                    <span className="coin-name">{coin.name}</span>
                    <span className="coin-price">${coin.usd_price ? formatPrice(coin.usd_price) : ''}</span>
                  </div>
                </div>
                <div className="coin-info-right-section">
                  {coin.prices && (
                    <Sparkline
                      color={getChartColorFromPrices(coin.prices)}
                      data={formatPricesToChartPoints(coin.prices)}
                      height={20}
                      width={80}
                    />
                  )}
                  <span
                    className={`coin-price-change-percent ${getClassnameByValue(coin.usd_price_change_24h)}`}
                    title={formatPercentage(coin.usd_price_change_24h)}
                  >
                    {coin.usd_price_change_24h ? formatPercentage(coin.usd_price_change_24h) : '–'}
                  </span>
                </div>
              </a>
            </div>
          ))}
        </Marquee>
      </div>
      <CryptoDotComLink />
    </CoinMarqueeContainer>
  );
};

const CoinMarqueeContainer = styled.div`
  &.coin-marquee-container {
    --border-color: rgba(119, 119, 119, 0.19);
    width: 100%;
    height: 118px;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    font-family:
      BlinkMacSystemFont,
      -apple-system,
      Segoe UI,
      Roboto,
      Oxygen,
      Ubuntu,
      Cantarell,
      Fira Sans,
      Droid Sans,
      Helvetica Neue,
      Helvetica,
      Arial,
      sans-serif;
    font-weight: 600;
    font-size: 14px;
    height: 84px;

    > .marquee,
    .marquee-container,
    .coin-marquee-wrapper {
      min-height: 85px;
    }

    .marquee-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      color: #000000;
      font-size: 12px;
      border: 1px solid var(--border-color);
      border-left: none;
      border-right: none;
      margin-top: 1px;
      max-width: 260px;
      width: 260px;
      min-width: 260px;
      height: 84px;
      padding: 0.5rem 1rem;
      box-sizing: border-box;
      position: relative;

      .hover-overlay {
        display: none;
        position: absolute;
        width: 100%;
        height: 100%;
        -webkit-box-align: center;
        align-items: center;
        -webkit-box-pack: center;
        justify-content: center;
        position: absolute;
        inset: 0px;
        background: rgba(25, 19, 19, 0.7);

        .hover-overlay-text {
          font-size: 14px;
          background: rgb(17, 153, 250);
          border-radius: 14px;
          line-height: 17px;
          color: #ffffff;
          padding: 5px 16px;
        }
      }

      &:hover {
        .hover-overlay {
          display: flex;
          animation-play-state: paused;
        }
      }

      &:active {
        .hover-overlay {
          animation-play-state: paused;
        }
      }

      &:after {
        content: ' ';
        display: block;
        border-right: 1px solid var(--border-color);
        position: absolute;
        right: 0;
        height: 60%;
      }

      > a {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }
    }

    .coin-info-left-section {
      display: flex;
    }

    .coin-info-right-section {
      display: flex;
      flex-direction: column;
    }

    .coin-name-symbol-container {
      display: flex;
      flex-direction: column;
      color: rgb(15, 28, 57);
      margin-inline-start: 1rem;
      margin-inline-end: 1rem;

      .coin-name {
        max-width: 100px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        margin-bottom: 0.25rem;
      }
    }

    .coin-image {
      width: 32px;
      height: 32px;
      align-self: center;
    }

    .coin-price {
      font-size: 18px;
    }

    .coin-price-change-percent {
      text-align: right;
      font-weight: 700;
      font-size: 14px;
      margin-top: 0.25rem;

      &.up {
        color: #20bca4;
      }

      &.down {
        color: #e64b60;
      }
    }
  }
`;
